import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import ManagementHeaderTabs from './ManagementHeaderTabs';
import ManagementProgressionTab from './ManagementProgressionTab';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getManagementPriorities,
  getAllUsersByProjectId,
  getUserById,
  editProject,
  getFollowUpsByProjectId,
  getEngineerPrioritiesByProjectId,
  getInventoryDailyTasks,
  updateManagementPriorities,
  getManagementDashboardStats
} from '../../services/api/implementation/impl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import sales from '../../../shared/assets/svg/sales.svg';
import operations from '../../../shared/assets/svg/operations.svg';
import inventory from '../../../shared/assets/svg/Inventory.svg';
import Multiselect from 'multiselect-react-dropdown';
import ActionCenterPanel from './ActionCenterPanel';
import AiPulsePanel from '../../components/ai/AiPulsePanel';
import ManagementDashboardFilters from './ManagementDashboardFilters';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  getProjectSetupNavigation,
  isProjectSetupReviewType,
  isProjectSetupType
} from '../../utils/projectStructurePriorities';
import {
  isInventorySetupReviewType,
  navigateInventorySetupReview
} from '../../utils/inventoryTaskNavigation';
import { isDominoTask } from '../../utils/dailyTaskTypes';
import {
  buildManagementHeaderTabParams,
  getManagementHeaderTab
} from './managementDashboardUrlParams';
import { isDominoEnabled } from '../../../shared/utils/authStorage';

const ManagementDashboard = ({ portfolioMode = false }) => {
  const { collapsed } = useSidebar();
  const { projectsForDropdown } = useProjectsList();
  const leftColumnRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftColumnHeight, setLeftColumnHeight] = useState(0);
  const [filters, setFilters] = useState({ projectID: null, month: null });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const headerTab = getManagementHeaderTab(searchParams);
  const isDashboardTab = headerTab === 'dashboard';
  const showProgressionTab = isDominoEnabled();
  const userID = Number(Cookies.get('userId'));
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem('engineerSelectedDate') || new Date().toISOString().split('T')[0]
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  // State for priority assignment modal
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedPriorityType, setSelectedPriorityType] = useState(null);
  const [selectedPriorityProjectId, setSelectedPriorityProjectId] = useState(null);
  const [selectedPriorityId, setSelectedPriorityId] = useState(null); // Store priority ID
  const queryClient = useQueryClient();

  const handleHeaderTabChange = useCallback(
    (tab) => {
      setSearchParams(buildManagementHeaderTabParams(searchParams, tab), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!showProgressionTab && getManagementHeaderTab(searchParams) === 'progression') {
      setSearchParams(buildManagementHeaderTabParams(searchParams, 'dashboard'), { replace: true });
    }
  }, [showProgressionTab, searchParams, setSearchParams]);

  useEffect(() => {
    // Track page view
    mixpanel.track('Management Dashboard Viewed', {
      user_id: userID,
      timestamp: new Date().toISOString()
    });
  }, [userID]);

  const { data: currentUser } = useQuery({
    queryKey: ['managementDashboardUser', userID],
    queryFn: () => getUserById(userID),
    enabled: !!userID
  });

  const {
    data: priorities,
    refetch: refetchPriorities,
    isLoading: isLoadingPriorities
  } = useQuery({
    queryKey: ['priorities', filters.projectID],
    queryFn: () => getManagementPriorities(filters.projectID, null),
    enabled: isDashboardTab,
    staleTime: 0, // Data is considered stale immediately
    gcTime: 0, // Don't cache the data
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Sales Escalations
  const {
    data: salesEscalations,
    refetch: refetchSalesEscalations,
    isLoading: isLoadingSalesEscalations
  } = useQuery({
    queryKey: ['salesEscalations', filters.projectID],
    queryFn: () => getFollowUpsByProjectId(filters.projectID, null),
    enabled: isDashboardTab && !!filters.projectID,
    onSuccess: (data) => {
      mixpanel.track('Sales Escalations API Success', {
        user_id: userID,
        project_id: filters.projectID,
        escalations_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Sales Escalations API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Engineer Escalations
  const {
    data: engineerEscalations,
    refetch: refetchEngineerEscalations,
    isLoading: isLoadingEngineerEscalations
  } = useQuery({
    queryKey: ['engineerEscalations', filters.projectID, selectedDate],
    queryFn: () => getEngineerPrioritiesByProjectId(filters.projectID, selectedDate),
    enabled: isDashboardTab && !!filters.projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Engineer Escalations API Success', {
        user_id: userID,
        project_id: filters.projectID,
        escalations_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Engineer Escalations API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Inventory Tasks
  const {
    data: inventoryTasks,
    refetch: refetchInventoryTasks,
    isLoading: isLoadingInventoryTasks
  } = useQuery({
    queryKey: ['inventoryTasks', filters.projectID, selectedDate],
    queryFn: () => getInventoryDailyTasks(filters.projectID, selectedDate),
    enabled: isDashboardTab && !!filters.projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Inventory Tasks API Success', {
        user_id: userID,
        project_id: filters.projectID,
        tasks_count: data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Inventory Tasks API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const isAllProjectsSelected = filters.projectID === '';

  const { data: dashboardStatsResponse, isLoading: isLoadingDashboardStats } = useQuery({
    queryKey: ['managementDashboardStats', filters.projectID],
    queryFn: () => getManagementDashboardStats(filters.projectID),
    enabled:
      isDashboardTab && filters.projectID !== null && filters.projectID !== undefined
  });

  const handleFiltersChange = useCallback(({ projectID, month }) => {
    setFilters({ projectID, month });
  }, []);

  const handleFiltersLoadingChange = useCallback((isLoading) => {
    setIsFiltersLoading(isLoading);
  }, []);

  // Fetch management users for assignment modal
  const { data: managementUsers, isLoading: isLoadingManagementUsers } = useQuery({
    queryKey: ['managementUsers', filters.projectID],
    queryFn: () => getAllUsersByProjectId(filters.projectID, 1, 1000000),
    enabled: isDashboardTab && showPriorityModal && !!filters.projectID
  });

  // Form for priority assignment
  const {
    register: registerPriority,
    handleSubmit: handleSubmitPriority,
    formState: { errors: priorityErrors },
    reset: resetPriority,
    watch: watchPriority,
    control: controlPriority
  } = useForm();

  const selectedUserId = watchPriority('userId');
  const selectedDay = watchPriority('day');
  const selectedAttendees = watchPriority('attendees') || [];

  // Mutation for updating management priority
  const { mutate: updatePriorityMutation, isPending: isUpdatingPriority } = useMutation({
    mutationFn: ({ id, payload }) => updateManagementPriorities(id, payload),
    onSuccess: () => {
      toast.success('Priority updated successfully');
      // Refetch priorities after successful update
      refetchPriorities();
      mixpanel.track('Management Priority Updated', {
        user_id: userID,
        priority_id: selectedPriorityId,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update priority');
      mixpanel.track('Management Priority Update Failed', {
        user_id: userID,
        priority_id: selectedPriorityId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Mutation for updating project
  const { mutate: updateProjectMutation, isPending: isUpdatingProject } = useMutation({
    mutationFn: (payload) => editProject(selectedPriorityProjectId, payload),
    onSuccess: async (_, payload) => {
      // After editProject succeeds, call updateManagementPriorities
      if (selectedPriorityId && payload) {
        updatePriorityMutation(
          { id: selectedPriorityId, payload: { status: 'Completed' } },
          {
            onSuccess: () => {
              // Invalidate and refetch priorities after priority update completes
              queryClient.invalidateQueries({ queryKey: ['priorities', filters.projectID] });
              refetchPriorities();
            }
          }
        );
      } else {
        // If no priority update needed, invalidate and refetch immediately
        queryClient.invalidateQueries({ queryKey: ['priorities', filters.projectID] });
        refetchPriorities();
      }

      setShowPriorityModal(false);
      resetPriority();
      setSelectedPriorityType(null);
      setSelectedPriorityProjectId(null);
      setSelectedPriorityId(null);
      mixpanel.track('Priority Assignment Updated', {
        user_id: userID,
        priority_type: selectedPriorityType,
        project_id: selectedPriorityProjectId,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update project');
      mixpanel.track('Priority Assignment Failed', {
        user_id: userID,
        priority_type: selectedPriorityType,
        project_id: selectedPriorityProjectId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Handle priority assignment form submission
  const onSubmitPriorityAssignment = (data) => {
    if (!selectedPriorityProjectId) {
      toast.error('Project ID is missing');
      return;
    }

    const payload = {};

    // Map priority type to payload key
    switch (selectedPriorityType) {
      case 'User Assign For CCR':
        payload.customerChangeRequestApprovalUser_id = parseInt(data.userId);
        break;
      case 'User Assign For Deal':
        payload.dealAppprovingUser_id = parseInt(data.userId);
        break;
      case 'User Assign For UC':
        payload.ucAppprovingUser_id = parseInt(data.userId);
        break;
      case 'Review Meeting Date':
        payload.reviewMeetingDate = parseInt(data.day);
        payload.organizerUser_id = parseInt(data.organizerUserId);
        // Extract user IDs from selected attendees array
        if (data.attendees && Array.isArray(data.attendees) && data.attendees.length > 0) {
          payload.attendees = data.attendees.map((attendee) =>
            typeof attendee === 'object' && attendee !== null
              ? parseInt(attendee.id)
              : parseInt(attendee)
          );
        } else {
          payload.attendees = [];
        }
        break;
      case 'Target Meeting Date':
        payload.targetMeetingDate = parseInt(data.day);
        payload.targetOrganizer_id = parseInt(data.targetOrganizerUserId);
        // Extract user IDs from selected target attendees array
        if (
          data.targetAttendees &&
          Array.isArray(data.targetAttendees) &&
          data.targetAttendees.length > 0
        ) {
          payload.targetAttendees = data.targetAttendees.map((attendee) =>
            typeof attendee === 'object' && attendee !== null
              ? parseInt(attendee.id)
              : parseInt(attendee)
          );
        } else {
          payload.targetAttendees = [];
        }
        break;
      default:
        toast.error('Invalid priority type');
        return;
    }

    // Store payload for use in updateManagementPriorities
    updateProjectMutation(payload);
  };

  // Check if priority type requires modal (not navigation)
  const requiresModal = (priorityType) => {
    return [
      'User Assign For CCR',
      'User Assign For Deal',
      'User Assign For UC',
      'Review Meeting Date',
      'Target Meeting Date'
    ].includes(priorityType);
  };

  // Close date picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Process Sales Escalations Data - return total count
  const getSalesEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.isEscalated && item.status === 'Active').length;
  };

  // Process Engineer Escalations Data - return total count
  const getEngineerEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.isEscalated && item.status !== 'Completed').length;
  };

  // Process Inventory Escalations Data - return total count
  const getInventoryEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.status !== 'Completed').length;
  };

  const getGreetingMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Check if a priority type is an approval type
  const isApprovalType = (priorityType) => {
    return (
      ['Deal Approval', 'Approve UC', 'Approve CCR', 'File Request'].includes(priorityType) ||
      isProjectSetupReviewType(priorityType)
    );
  };

  const navigateProjectStructurePriority = (item, projectId) => {
    const targetProjectId = item.items?.[0]?.project_id || projectId;
    const nav = getProjectSetupNavigation(targetProjectId, item.priorityType);
    if (!nav) {
      toast.error('Project not found for this priority');
      return;
    }
    localStorage.setItem('secondaryTab', nav.localStorageTab);
    navigate(nav.path, { state: nav.state });
  };

  // Process Priorities Data - separate approvals from other priorities
  // When no project filter: show each priority individually with project name, group approvals by project
  // When project filter applied: group priorities by title, group approvals by type
  const processPrioritiesData = (data) => {
    const hasProjectFilter = filters.projectID && filters.projectID !== '';

    // If no project filter
    if (!hasProjectFilter) {
      const prioritiesData = [];
      const approvalsByProject = {}; // Group approvals by project_id

      data.forEach((item) => {
        // Skip completed and domino tasks
        if (item.status === 'Completed' || isDominoTask(item)) return;

        const priorityType = item.type || 'Unknown';
        const priorityTitle = item.title || item.type || 'Unknown';

        if (isApprovalType(priorityType)) {
          // Group approvals by project_id
          const projectId = item.project_id || 'unknown';
          if (!approvalsByProject[projectId]) {
            approvalsByProject[projectId] = {
              projectId: projectId,
              projectName: item.project?.name || 'Unknown Project',
              items: []
            };
          }
          approvalsByProject[projectId].items.push(item);
        } else {
          prioritiesData.push({
            type: 'priority',
            priorityType: priorityType,
            priorityTitle: priorityTitle,
            count: 1,
            items: [item]
          });
        }
      });

      // Convert approvals grouped by project to display format
      const approvalsData = Object.values(approvalsByProject)
        .map((projectGroup) => {
          // Group items within each project by type
          const itemsByType = {};
          projectGroup.items.forEach((item) => {
            const priorityType = item.type || 'Unknown';
            if (!itemsByType[priorityType]) {
              itemsByType[priorityType] = [];
            }
            itemsByType[priorityType].push(item);
          });

          // Create approval entries for each type within the project
          return Object.entries(itemsByType).map(([priorityType, items]) => ({
            type: 'approval',
            priorityType: priorityType,
            priorityTitle: items[0]?.title || priorityType,
            count: items.length,
            items: items,
            projectId: projectGroup.projectId,
            projectName: projectGroup.projectName
          }));
        })
        .flat();

      return { priorities: prioritiesData, approvals: approvalsData };
    }

    // If project filter is applied
    // Priorities: group by title
    // Approvals: group by type (not title)
    const groupedByTitle = {};
    const approvalGroups = {};

    data.forEach((item) => {
      // Skip completed and domino tasks
      if (item.status === 'Completed' || isDominoTask(item)) return;

      const priorityType = item.type || 'Unknown';
      const priorityTitle = item.title || item.type || 'Unknown';

      // Separate approval types
      if (isApprovalType(priorityType)) {
        // Group approvals by type (not title)
        if (!approvalGroups[priorityType]) {
          approvalGroups[priorityType] = {
            type: priorityType,
            title: priorityTitle,
            count: 0,
            items: []
          };
        }
        approvalGroups[priorityType].count++;
        approvalGroups[priorityType].items.push(item);
      } else {
        if (!groupedByTitle[priorityTitle]) {
          groupedByTitle[priorityTitle] = {
            type: priorityType,
            title: priorityTitle,
            count: 0,
            items: []
          };
        }
        groupedByTitle[priorityTitle].count++;
        groupedByTitle[priorityTitle].items.push(item);
      }
    });

    const prioritiesData = Object.values(groupedByTitle)
      .sort((a, b) => b.count - a.count)
      .map((group) => ({
        type: 'priority',
        priorityType: group.type,
        priorityTitle: group.title,
        count: group.count,
        items: group.items
      }));
    const approvalsData = Object.values(approvalGroups).map((group) => ({
      type: 'approval',
      priorityType: group.type,
      priorityTitle: group.title,
      count: group.count,
      items: group.items
    }));

    return { priorities: prioritiesData, approvals: approvalsData };
  };

  const handlePriorityItemClick = (item, projectId) => {
    mixpanel.track('Priority Item Clicked', {
      user_id: userID,
      priority_type: item.priorityType,
      count: item.count,
      project_id: filters.projectID,
      timestamp: new Date().toISOString()
    });

    if (requiresModal(item.priorityType)) {
      setSelectedPriorityType(item.priorityType);
      setSelectedPriorityProjectId(projectId);
      setSelectedPriorityId(item.items?.[0]?.id || null);
      setShowPriorityModal(true);
      return;
    }

    const navProjectID = filters.projectID || projectId;

    if (item.priorityType === 'Onboarding Company' || item.priorityType === 'Onboarding Project') {
      navigate('/builder/management/company');
      return;
    }

    if (item.priorityType?.startsWith('Onboarding First User')) {
      navigate('/builder/users', {
        state: {
          projectID: item.items?.[0]?.project_id || navProjectID,
          vertical: item.items?.[0]?.tag || null
        }
      });
      return;
    }

    if (isProjectSetupType(item.priorityType) || isProjectSetupReviewType(item.priorityType)) {
      navigateProjectStructurePriority(item, projectId);
      return;
    }

    if (isInventorySetupReviewType(item.priorityType)) {
      const priorityItem = item.items?.[0];
      const stockProjectId = priorityItem?.project_id || projectId;
      const priorityId = priorityItem?.id;

      if (!stockProjectId || !priorityId) {
        toast.error('Project or priority not found');
        return;
      }

      updateManagementPriorities(priorityId, { status: 'Completed' })
        .then(() => {
          toast.success('Inventory setup review completed');
          refetchPriorities();
          navigateInventorySetupReview(navigate, stockProjectId, {
            priorityId,
            inventorySetupReviewCompleted: true
          });
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to complete inventory setup review'
          );
        });
      return;
    }

    if (item.priorityType === 'Review Meeting') {
      navigate('/builder/management/review-meeting', {
        state: {
          projectID: navProjectID,
          month: filters.month,
          priorityId: item.items?.[0]?.id || null
        }
      });
    } else if (item.priorityType === 'Set Targets') {
      navigate('/builder/management/set-target', {
        state: {
          projectID: navProjectID,
          month: filters.month,
          priorityId: item.items?.[0]?.id || null
        }
      });
    } else {
      navigate('/builder/management/priorities', {
        state: {
          priorityType: item.priorityType,
          projectID: navProjectID,
          selectedDate: localStorage.getItem('engineerSelectedDate') || selectedDate
        }
      });
    }
  };

  const handleApprovalItemClick = (item, projectId) => {
    mixpanel.track('Approval Item Clicked', {
      user_id: userID,
      approval_type: item.priorityType,
      count: item.count,
      project_id: filters.projectID,
      timestamp: new Date().toISOString()
    });

    navigate('/builder/management/priorities', {
      state: {
        priorityType: item.priorityType,
        projectID: filters.projectID || projectId,
        selectedDate: localStorage.getItem('engineerSelectedDate') || selectedDate
      }
    });
  };

  const handleEscalationClick = (escalationType) => {
    const escalationCount =
      escalationType === 'salesEscalations'
        ? getSalesEscalationsCount(salesEscalations?.data)
        : escalationType === 'engineerEscalations'
          ? getEngineerEscalationsCount(engineerEscalations?.data)
          : getInventoryEscalationsCount(inventoryTasks);

    mixpanel.track(
      escalationType === 'salesEscalations'
        ? 'Sales Escalation Clicked'
        : escalationType === 'engineerEscalations'
          ? 'Engineer Escalation Clicked'
          : 'Inventory Escalation Clicked',
      {
        user_id: userID,
        count: escalationCount,
        project_id: filters.projectID,
        timestamp: new Date().toISOString()
      }
    );

    navigate('/builder/management/priorities', {
      state: {
        escalationType,
        projectID: filters.projectID,
        selectedDate: selectedDate
      }
    });
  };

  const dashboardStats = dashboardStatsResponse?.data || {};
  const salesPulseData = dashboardStats?.sales || {};
  const salesPulseCards = [
    { label: 'Hot', value: salesPulseData.Hot ?? salesPulseData.hot ?? 0, dotClass: 'bg-red-500' },
    { label: 'Confirmed', value: salesPulseData.confirmed ?? 0, dotClass: 'bg-blue-600' },
    { label: 'Closed Won', value: salesPulseData.closedWon ?? 0, dotClass: 'bg-cyan-300' },
    { label: 'Cold', value: salesPulseData.cold ?? 0, dotClass: 'bg-gray-500' }
  ];

  const activeProjectsCount =
    dashboardStats?.totalProjects ?? projectsForDropdown?.length ?? 0;
  const unitsData = dashboardStats?.units || {};
  const totalUnitsCount = unitsData?.totalUnits ?? 0;
  const vacantUnitsCount = unitsData?.vacant ?? 0;
  const bookedUnitsCount = unitsData?.booked ?? 0;
  const salesDeedCompleteUnitsCount = unitsData?.salesDeedCompleted ?? 0;
  const unitStatusCards = [
    { label: 'Total Units', value: totalUnitsCount, dotClass: 'bg-cyan-500' },
    { label: 'Vacant', value: vacantUnitsCount, dotClass: 'bg-amber-500' },
    { label: 'Booked', value: bookedUnitsCount, dotClass: 'bg-violet-500' },
    { label: 'Sales Deed Completed', value: salesDeedCompleteUnitsCount, dotClass: 'bg-emerald-500' }
  ];

  const operationsStats = dashboardStats?.operations || {};
  const liveTasksByStatus = operationsStats?.liveTasksByStatus || {};
  const operationsCards = [
    { label: 'To do', value: liveTasksByStatus.toDo ?? 0, dotClass: 'bg-slate-500' },
    { label: 'In progress', value: liveTasksByStatus.inProgress ?? 0, dotClass: 'bg-sky-600' },
    { label: 'Completed', value: liveTasksByStatus.completed ?? 0, dotClass: 'bg-emerald-500' },
    { label: 'Delayed tasks', value: operationsStats.delayedTasks ?? 0, dotClass: 'bg-amber-500' }
  ];

  const inventoryStats = dashboardStats?.inventory || {};
  const totalSkus = inventoryStats?.totalSkus;
  const stockHealth = inventoryStats?.stockHealth || {};
  const formatStockPercent = (p) => {
    const n = Number(p);
    if (Number.isNaN(n)) return '0%';
    return `${n % 1 === 0 ? n : n.toFixed(2)}%`;
  };
  const stockHealthItems = [
    { key: 'red', label: 'Red', rowClass: 'bg-rose-50 border-rose-200', dotClass: 'bg-red-600' },
    { key: 'orange', label: 'Orange', rowClass: 'bg-orange-50 border-orange-200', dotClass: 'bg-orange-500' },
    { key: 'yellow', label: 'Yellow', rowClass: 'bg-amber-50 border-amber-200', dotClass: 'bg-amber-400' },
    { key: 'green', label: 'Green', rowClass: 'bg-emerald-50 border-emerald-200', dotClass: 'bg-emerald-600' }
  ];

  const formatInr = (amount) => {
    const n = Number(amount);
    if (Number.isNaN(n)) {
      return '₹0';
    }
    return `₹${n.toLocaleString('en-IN')}`;
  };
  const collectionDueAmount = dashboardStats?.collectionDue;
  const overdueCollectionAmount = dashboardStats?.overdueCollection;

  const dashboardCountCards = [
    {
      label: 'Collection Due',
      value: formatInr(collectionDueAmount),
      accent: 'bg-teal-600',
      isCurrency: true
    },
    {
      label: 'Overdue Collection',
      value: formatInr(overdueCollectionAmount),
      accent: 'bg-rose-600',
      isCurrency: true
    }
  ];
  if (isAllProjectsSelected) {
    dashboardCountCards.unshift({
      label: 'Active Projects',
      value: activeProjectsCount,
      accent: 'bg-cyan-500'
    });
  }

  const renderCountCardIcon = (label) => {
    if (label === 'Active Projects') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 21h18M5 21V7a1 1 0 011-1h4a1 1 0 011 1v14m0 0h8V11a1 1 0 00-1-1h-6"
          />
        </svg>
      );
    }

    if (label === 'Collection Due') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a1 1 0 11-2 0 1 1 0 012 0z"
          />
        </svg>
      );
    }

    if (label === 'Overdue Collection') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    }

    if (label === 'Total Units' || label === 'Vacant Units' || label === 'Booked Units') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      );
    }

    if (label === 'Sales Deed Complete') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    }

    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v3m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z"
        />
      </svg>
    );
  };

  useEffect(() => {
    if (headerTab !== 'dashboard') return;

    const leftColumnElement = leftColumnRef.current;
    if (!leftColumnElement) return;

    const updateLeftColumnHeight = () => {
      setLeftColumnHeight(Math.ceil(leftColumnElement.getBoundingClientRect().height));
    };

    updateLeftColumnHeight();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateLeftColumnHeight)
        : null;

    resizeObserver?.observe(leftColumnElement);
    window.addEventListener('resize', updateLeftColumnHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateLeftColumnHeight);
    };
  }, [
    headerTab,
    isLoadingDashboardStats,
    isLoadingPriorities,
    filters.projectID,
    dashboardCountCards.length
  ]);

  return (
    <Layout>
      <Loader
        isVisible={
          isSubmitting ||
          (isDashboardTab &&
            (isFiltersLoading ||
              isLoadingPriorities ||
              isLoadingManagementUsers ||
              isUpdatingProject ||
              isUpdatingPriority ||
              isLoadingSalesEscalations ||
              isLoadingEngineerEscalations ||
              isLoadingInventoryTasks ||
              isLoadingDashboardStats))
        }
      />

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header data={{ name: '' }} showProjectSelector={false}>
          {showProgressionTab && (
            <ManagementHeaderTabs activeTab={headerTab} onTabChange={handleHeaderTabChange} />
          )}
        </Header>
      </div>

      {/* Body Content */}
      {headerTab === 'dashboard' && (
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="w-full pt-[5rem] pl-5 pr-5 pb-2">
          <div className="w-full p-5">
            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0">
                <h2 className="text-4xl font-semibold text-[#1E4A53] leading-tight">
                  {getGreetingMessage()},{' '}
                  {currentUser?.name || currentUser?.userName || Cookies.get('userName') || 'User'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Track priorities, approvals, escalations, and sales momentum across your projects.
                </p>
              </div>
              <div className="flex justify-end w-[360px]">
                <div className="w-[360px]">
                  <ManagementDashboardFilters
                    onFiltersChange={handleFiltersChange}
                    onLoadingChange={handleFiltersLoadingChange}
                    includeAllProjectsOption
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-flex-start items-start relative p-2 gap-7 pl-5 min-w-0 w-full">
          {/* Left Side - Configuration Cards — flex-1 so Action Center can keep a fixed width */}
          <div
            ref={leftColumnRef}
            className="flex min-w-0 flex-1 flex-col gap-5 transition-all duration-300 ease-in-out">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {dashboardCountCards.map((card) => (
                <div
                  key={card.label}
                  className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      {card.label}
                    </p>
                    <span
                      className={`w-7 h-7 rounded-lg ${card.accent} text-white inline-flex items-center justify-center`}>
                      {renderCountCardIcon(card.label)}
                    </span>
                  </div>
                  <p
                    className={`mt-3 font-bold text-[#0f2a45] ${
                      card.isCurrency
                        ? 'text-xl sm:text-2xl break-words leading-tight'
                        : 'text-3xl'
                    }`}>
                    {card.value}
                  </p>
                </div>
              ))}
            </div>
            <AiPulsePanel
              projectId={filters.projectID}
              defaultVertical={portfolioMode ? undefined : 'management'}
              showAllVerticals={portfolioMode}
            />
            <div className="w-full bg-[#fff] border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-semibold text-[#1E4A53]">Sales Pulse</h3>
                  <p className="text-sm text-gray-500">Lead conversion stages for current period</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/builder/management/configure', {
                      state: {
                        selectedCard: 'Sales',
                        projectID: filters.projectID,
                        month: filters.month
                      }
                    });
                    mixpanel.track('Configure Card Clicked', {
                      user_id: userID,
                      card_type: 'Sales',
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}
                  className="bg-[#1f64d7] text-white text-sm font-semibold px-5 py-2 rounded-xl hover:bg-[#1a56bb] transition-colors inline-flex items-center gap-2">
                  Open Sales
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 17L17 7m0 0H9m8 0v8"
                    />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {salesPulseCards.map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-2xl border p-3 ${
                      item.label === 'Closed Won'
                        ? 'bg-[#134f52] text-white border-[#134f52]'
                        : 'bg-[#e9eef7] border-gray-200'
                    }`}>
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <span className={`w-2 h-2 rounded-full ${item.dotClass}`} />
                      <span
                        className={item.label === 'Closed Won' ? 'text-[#9de6dd]' : 'text-gray-700'}>
                        {item.label}
                      </span>
                    </div>
                    <div className="mt-4">
                      <p
                        className={`text-4xl font-semibold leading-none ${
                          item.label === 'Closed Won' ? 'text-white' : 'text-[#0d223a]'
                        }`}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full bg-[#fff] border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-semibold text-[#1E4A53]">Unit Status</h3>
                  <p className="text-sm text-gray-500">
                    Snapshot of inventory occupancy and completion status
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {unitStatusCards.map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-2xl border p-3 ${
                      item.label === 'Sales Deed Completed'
                        ? 'bg-[#124a43] text-white border-[#124a43]'
                        : 'bg-[#eef4fa] border-gray-200'
                    }`}>
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <span className={`w-2 h-2 rounded-full ${item.dotClass}`} />
                      <span
                        className={
                          item.label === 'Sales Deed Completed' ? 'text-[#b8f2d4]' : 'text-gray-700'
                        }>
                        {item.label}
                      </span>
                    </div>
                    <div className="mt-4">
                      <p
                        className={`text-4xl font-semibold leading-none ${
                          item.label === 'Sales Deed Completed' ? 'text-white' : 'text-[#0d223a]'
                        }`}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-[#1E4A53]">Operations</h3>
                  <p className="mt-0.5 text-sm text-gray-500">Live tasks by status and delayed work</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/builder/operations/dashboard', {
                      state: { projectID: filters.projectID, month: filters.month }
                    });
                    mixpanel.track('Management Dashboard Open Operations', {
                      user_id: userID,
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#1f64d7] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1a56bb]">
                  Open Operations
                  <img src={operations} alt="" className="h-4 w-4 brightness-0 invert" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                {operationsCards.map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-2xl border p-3 ${
                      item.label === 'Delayed tasks'
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-gray-200 bg-[#eef4fa]'
                    }`}>
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <span className={`h-2 w-2 rounded-full ${item.dotClass}`} />
                      <span
                        className={item.label === 'Delayed tasks' ? 'text-amber-900' : 'text-gray-700'}>
                        {item.label}
                      </span>
                    </div>
                    <p
                      className={`mt-4 text-4xl font-semibold leading-none ${
                        item.label === 'Delayed tasks' ? 'text-amber-950' : 'text-[#0d223a]'
                      }`}>
                      {Number(item.value).toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-[#1E4A53]">Inventory</h3>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {totalSkus != null && totalSkus !== '' ? (
                      <span className="font-medium text-gray-700">
                        {Number(totalSkus).toLocaleString('en-IN')} total SKUs
                      </span>
                    ) : null}
                    {totalSkus != null && totalSkus !== '' ? (
                      <span className="text-gray-500"> · Stock health by level</span>
                    ) : (
                      'Stock health by level'
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/builder/inventory/dashboard', {
                      state: { projectID: filters.projectID, month: filters.month }
                    });
                    mixpanel.track('Management Dashboard Open Inventory', {
                      user_id: userID,
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#1f64d7] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1a56bb]">
                  Open Inventory
                  <img src={inventory} alt="" className="h-4 w-4 brightness-0 invert" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                {stockHealthItems.map((item) => {
                  const row = stockHealth[item.key] || {};
                  const count = row.count ?? 0;
                  const pct = formatStockPercent(row.percent);
                  return (
                    <div
                      key={item.key}
                      className={`rounded-2xl border p-3 ${item.rowClass}`}>
                      <div className="flex items-center gap-2 text-xs font-semibold">
                        <span className={`h-2 w-2 rounded-full ${item.dotClass}`} />
                        <span className="text-gray-800">{item.label}</span>
                      </div>
                      <p className="mt-4 text-4xl font-semibold leading-none text-[#0d223a]">
                        {Number(count).toLocaleString('en-IN')}
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-600">{pct}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* isAllProjectsSelected && (
              <div className="w-full">
                <div className="grid grid-cols-1 gap-5">
                  <div
                    className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                    onClick={() => {
                      navigate('/builder/management/configure', {
                        state: {
                          selectedCard: 'Sales',
                          projectID: filters.projectID,
                          month: filters.month
                        }
                      });
                      mixpanel.track('Configure Card Clicked', {
                        user_id: userID,
                        card_type: 'Sales',
                        project_id: filters.projectID,
                        timestamp: new Date().toISOString()
                      });
                    }}>
                    <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                      <img src={sales} alt="Sales" className="w-12 h-12 brightness-0 invert" />
                    </div>
                    <div className="flex-1 w-full">
                      <p className="text-xl font-bold text-gray-800 mb-1">Sales</p>
                      <p className="text-sm text-gray-600">
                        View sales reports and graphs to analyze performance and trends
                      </p>
                    </div>
                  </div>

                    className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                    onClick={() => {
                      navigate('/builder/operations/dashboard', {
                        state: {
                          projectID: filters.projectID,
                          month: filters.month
                        }
                      });
                      mixpanel.track('Operations Card Clicked', {
                        user_id: userID,
                        card_type: 'Operations',
                        project_id: filters.projectID,
                        timestamp: new Date().toISOString()
                      });
                    }}>
                    <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                      <img
                        src={operations}
                        alt="Operations"
                        className="w-12 h-12 brightness-0 invert"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <p className="text-xl font-bold text-gray-800 mb-1">Operations</p>
                      <p className="text-sm text-gray-600">
                        View operations reports and graphs to track efficiency and productivity
                      </p>
                    </div>
                  </div>

                  <div
                    className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                    onClick={() => {
                      navigate('/builder/inventory/dashboard', {
                        state: {
                          projectID: filters.projectID,
                          month: filters.month
                        }
                      });
                      mixpanel.track('Inventory Card Clicked', {
                        user_id: userID,
                        card_type: 'Inventory',
                        project_id: filters.projectID,
                        timestamp: new Date().toISOString()
                      });
                    }}>
                    <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                      <img
                        src={inventory}
                        alt="Inventory"
                        className="w-12 h-12 brightness-0 invert"
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <p className="text-xl font-bold text-gray-800 mb-1">Inventory</p>
                      <p className="text-sm text-gray-600">
                        View inventory reports and graphs to monitor stock levels and usage
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) */}
            
          </div>

          <div className="w-[min(100%,31.25rem)] shrink-0 self-start">
            <ActionCenterPanel
              priorities={priorities}
              processPrioritiesData={processPrioritiesData}
              filters={filters}
              salesEscalations={salesEscalations}
              engineerEscalations={engineerEscalations}
              inventoryTasks={inventoryTasks}
              getSalesEscalationsCount={getSalesEscalationsCount}
              getEngineerEscalationsCount={getEngineerEscalationsCount}
              getInventoryEscalationsCount={getInventoryEscalationsCount}
              onPriorityClick={handlePriorityItemClick}
              onApprovalClick={handleApprovalItemClick}
              onEscalationClick={handleEscalationClick}
              panelHeight={leftColumnHeight}
            />
          </div>
        </div>
      </div>
      )}

      {showProgressionTab && headerTab === 'progression' && (
        <div className="transition-all duration-300 ease-in-out" style={{ marginTop: '4.1rem' }}>
          <ManagementProgressionTab
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onFiltersLoadingChange={handleFiltersLoadingChange}
          />
        </div>
      )}

      {/* Priority Assignment Modal */}
      {showPriorityModal && (
        <Modal
          name={
            selectedPriorityType === 'Review Meeting Date' ||
            selectedPriorityType === 'Target Meeting Date'
              ? `Assign ${selectedPriorityType}`
              : `Assign User for ${selectedPriorityType}`
          }
          width={50}
          onClose={() => {
            setShowPriorityModal(false);
            resetPriority();
            setSelectedPriorityType(null);
            setSelectedPriorityProjectId(null);
            setSelectedPriorityId(null);
          }}>
          <form onSubmit={handleSubmitPriority(onSubmitPriorityAssignment)}>
            {selectedPriorityType === 'Review Meeting Date' ||
            selectedPriorityType === 'Target Meeting Date' ? (
              // Calendar for day selection (1-28)
              <div className="space-y-4">
                <div
                  className="flex-shrink-0 flex flex-col gap-2 text-gray-700"
                  style={{ width: '100%' }}>
                  <div className="flex justify-between items-start space-x-2 w-fit">
                    <span className="text-gray-600">Day of Month</span>
                    <span className="text-red-500">*</span>
                  </div>
                  <Controller
                    name="day"
                    control={controlPriority}
                    rules={{
                      required: 'Day is required',
                      validate: (value) => {
                        if (!value) return 'Day is required';
                        const day = parseInt(value, 10);
                        if (isNaN(day) || day < 1 || day > 28) {
                          return 'Day must be a number between 1 and 28';
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => {
                      const selectedDay = field.value ? parseInt(field.value, 10) : null;
                      const showWarning = selectedDay && selectedDay > 5 && selectedDay <= 28;

                      // Calculate which day of the week the 1st of current month falls on
                      const today = new Date();
                      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                      const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.

                      // Generate calendar cells
                      const calendarCells = [];

                      // Add empty cells for days before the 1st
                      for (let i = 0; i < startingDayOfWeek; i++) {
                        calendarCells.push({ day: null, isEmpty: true });
                      }

                      // Add days 1-28
                      for (let day = 1; day <= 28; day++) {
                        calendarCells.push({ day, isEmpty: false });
                      }

                      return (
                        <div>
                          {/* Calendar Grid */}
                          <div className="grid grid-cols-7 gap-2 p-4 border border-gray-200 rounded-lg bg-white">
                            {/* Day headers */}
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
                              <div
                                key={dayName}
                                className="text-center text-xs font-semibold text-gray-600 py-1.5">
                                {dayName}
                              </div>
                            ))}

                            {/* Calendar cells */}
                            {calendarCells.map((cell, index) => {
                              if (cell.isEmpty) {
                                return <div key={`empty-${index}`} className="h-10" />;
                              }

                              const day = cell.day;
                              const isSelected = selectedDay === day;
                              const isOptimal = day >= 1 && day <= 5;
                              const isNonOptimal = day > 5 && day <= 28;

                              return (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => {
                                    field.onChange(day.toString());
                                  }}
                                  className={`
                                    h-10 w-full rounded-lg text-xs font-medium transition-all
                                    flex items-center justify-center
                                    ${
                                      isSelected
                                        ? 'bg-primary text-white shadow-md ring-2 ring-primary'
                                        : isOptimal
                                          ? 'bg-white text-gray-900 hover:bg-gray-100 border border-gray-300'
                                          : 'bg-white text-gray-600 hover:bg-yellow-50 border border-yellow-300'
                                    }
                                  `}>
                                  {day}
                                </button>
                              );
                            })}
                          </div>
                          {/* Warning message for dates outside optimal range */}
                          {showWarning && (
                            <p className="text-yellow-600 text-sm mt-3 flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                              </svg>
                              Optimal date for the meeting is from 1 to 5 of the month
                            </p>
                          )}
                        </div>
                      );
                    }}
                  />
                  {priorityErrors?.day && (
                    <p className="text-red-500 text-sm mt-1">{priorityErrors.day.message}</p>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Select the day of the month (1-28) for the{' '}
                  {selectedPriorityType === 'Review Meeting Date'
                    ? 'review meeting'
                    : 'target meeting'}
                </p>

                {/* Organizer User and Attendees fields - for both Review Meeting Date and Target Meeting Date */}
                {(selectedPriorityType === 'Review Meeting Date' ||
                  selectedPriorityType === 'Target Meeting Date') && (
                  <div className="mt-4 space-y-4">
                    {isLoadingManagementUsers ? (
                      <p className="text-sm text-gray-500">Loading users...</p>
                    ) : (
                      <>
                        <FormInput
                          width={100}
                          label="Organizer User"
                          register={registerPriority}
                          register_key={
                            selectedPriorityType === 'Review Meeting Date'
                              ? 'organizerUserId'
                              : 'targetOrganizerUserId'
                          }
                          type="dropdown"
                          options={
                            managementUsers?.data?.map((user) => ({
                              value: user.id,
                              label: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                            })) || []
                          }
                          isRequired={true}
                          validation={{ required: 'Organizer user is required' }}
                          errors={priorityErrors}
                        />
                        <Controller
                          name={
                            selectedPriorityType === 'Review Meeting Date'
                              ? 'attendees'
                              : 'targetAttendees'
                          }
                          control={controlPriority}
                          defaultValue={[]}
                          rules={{
                            required: 'At least one attendee is required',
                            validate: (value) => {
                              if (!value || !Array.isArray(value) || value.length === 0) {
                                return 'At least one attendee is required';
                              }
                              return true;
                            }
                          }}
                          render={({ field, fieldState }) => (
                            <div
                              className="flex-shrink-0 flex flex-col gap-2 text-gray-600"
                              style={{ width: '100%' }}>
                              <div className="flex justify-between items-start space-x-2 w-fit">
                                <span className="text-gray-600">Select Meeting Attendees</span>
                                <span className="text-red-500">*</span>
                              </div>
                              <Multiselect
                                options={
                                  managementUsers?.data?.map((user) => ({
                                    id: user.id,
                                    name: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                                  })) || []
                                }
                                selectedValues={field.value || []}
                                onSelect={(selectedList) => {
                                  field.onChange(selectedList);
                                }}
                                onRemove={(selectedList) => {
                                  field.onChange(selectedList);
                                }}
                                displayValue="name"
                                placeholder="Select attendees"
                                style={{
                                  optionContainer: {
                                    border: '1px solid',
                                    backgroundColor: 'white'
                                  },
                                  option: {
                                    color: 'black',
                                    backgroundColor: 'white'
                                  },
                                  chips: {
                                    background: '#2A565E',
                                    color: 'white'
                                  }
                                }}
                              />
                              {fieldState.error && (
                                <p className="text-red-500 text-sm mt-1">
                                  {fieldState.error.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // User dropdown
              <div className="space-y-4">
                {isLoadingManagementUsers ? (
                  <p className="text-sm text-gray-500">Loading users...</p>
                ) : (
                  <FormInput
                    width={100}
                    label="Select User"
                    register={registerPriority}
                    register_key="userId"
                    type="dropdown"
                    options={
                      managementUsers?.data?.map((user) => ({
                        value: user.id,
                        label: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                      })) || []
                    }
                    isRequired={true}
                    validation={{ required: 'User is required' }}
                    errors={priorityErrors}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setShowPriorityModal(false);
                  resetPriority();
                  setSelectedPriorityType(null);
                  setSelectedPriorityProjectId(null);
                  setSelectedPriorityId(null);
                }}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdatingProject || isUpdatingPriority}>
                {isUpdatingProject || isUpdatingPriority ? 'Updating...' : 'Assign'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default ManagementDashboard;
